import * as React from 'react';
import { useTranslation } from 'next-i18next';
import { useQuery } from '@tanstack/react-query';
import { InformationCircleIcon, CreditCardIcon, PlusIcon } from '@heroicons/react/24/outline';
import { useAccountsService, useEmployersService } from '@restworld/data-services';
import { useAuth } from '@restworld/feature-authentication';
import { toast } from 'react-toastify';
import { Button as ButtonV2, Link, Heading, IconButton } from '@restworld/ui-ds-base';
import { cn, useKv } from '@restworld/utils-common';
import { useSetAtom } from 'jotai';
import PaymentSuccessModal from './payment-success-modal';
import ConsumptionDetailModal from './consumption-detail-modal';
import { consumptionDetailModalOpenAtom } from './consumption-detail-modal/atoms';
import { getPayInvoiceErrorMessageKey } from '../employer-invoices-v2/employer-invoices.hooks';
import { ApiError } from '@restworld/utility-types';

const NextPaymentDetails: React.FunctionComponent = () => {
  const { t } = useTranslation('payment-and-invoices');
  const accountsService = useAccountsService();
  const employersService = useEmployersService();
  const { user } = useAuth();
  const { setKV, getKV } = useKv();
  const [showSuccessModal, setShowSuccessModal] = React.useState(false);
  const [shouldCheckPayment, setShouldCheckPayment] = React.useState(false);
  const [isCreatingCheckout, setIsCreatingCheckout] = React.useState(false);
  const setConsumptionDetailOpen = useSetAtom(consumptionDetailModalOpenAtom);

  const pendingPaymentKey = React.useMemo(
    () => `pending_payment_check_${user?.organization_id}`,
    [user?.organization_id]
  );

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['nextPaymentDetails'],
    queryFn: async () => {
      const response = await accountsService.getNextPaymentDetails();
      return response.data;
    },
    retry: 5,
    retryDelay: 1000,
  });

  React.useEffect(() => {
    if (isError) {
      toast.error(t('next_payment_details_error'));
    }
  }, [isError, t]);

  // Check KV store on mount and when page becomes visible
  React.useEffect(() => {
    const checkPendingPayment = async () => {
      if (!user?.organization_id) return;

      const kvData = await getKV(pendingPaymentKey);
      if (kvData?.value === 'true') {
        setShouldCheckPayment(true);
      }
    };

    // Check on mount
    checkPendingPayment();

    // Check when tab becomes visible
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        checkPendingPayment();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [user?.organization_id, getKV, pendingPaymentKey]);

  // When shouldCheckPayment is true, refetch and check if payment is cleared
  React.useEffect(() => {
    if (shouldCheckPayment && !isLoading) {
      refetch().then(async ({ data: refetchedData }) => {
        const isUnpaid = refetchedData?.type === 'unpaid';

        if (!isUnpaid) {
          // Payment is cleared (moved to upcoming or no data), show success modal
          setShowSuccessModal(true);
          // Clear KV store
          await setKV({ key: pendingPaymentKey, value: 'false' });
        }
        setShouldCheckPayment(false);
      });
    }
  }, [shouldCheckPayment, isLoading, refetch, setKV, pendingPaymentKey]);

  const invoiceId = data?.invoice_id;
  const handlePayInvoice = React.useCallback(async () => {
    if (!invoiceId || !user?.organization_id) return;

    // Open the tab synchronously, inside the click: browsers block a window.open
    // issued after an await. It is pointed at the checkout session once we have it.
    const payWindow = window.open('', '_blank');
    setIsCreatingCheckout(true);
    try {
      // The checkout session restricts the invoice to card + PayPal; the raw
      // hosted invoice page would also offer SEPA.
      const { url } = (await employersService.createCheckoutSession(invoiceId)).data;
      if (!url) throw new Error(`Missing checkout session url for invoice ${invoiceId}`);

      // Store pending payment check in KV store. It only drives the "payment received" modal on
      // return, so a failure here must not abort a payment whose checkout URL is already valid.
      try {
        await setKV({ key: pendingPaymentKey, value: 'true' });
      } catch (kvError) {
        console.warn(`Could not store the pending payment flag for invoice ${invoiceId}`, kvError);
      }
      if (payWindow) {
        payWindow.opener = null;
        payWindow.location.href = url;
      } else {
        // Popup blocked: continue the payment in the current tab.
        window.location.href = url;
      }
    } catch (error) {
      console.error(`Error creating checkout session for invoice ${invoiceId}`, error);
      payWindow?.close();
      toast.error(t(getPayInvoiceErrorMessageKey(error as ApiError)));
    } finally {
      setIsCreatingCheckout(false);
    }
  }, [invoiceId, user?.organization_id, employersService, setKV, pendingPaymentKey, t]);

  const handleCloseSuccessModal = React.useCallback(() => {
    setShowSuccessModal(false);
  }, []);

  if (isLoading) {
    return (
      <Section>
        <SectionHeader>
          <TitleContainer>
            <Heading color="neutral">{t('next_payment_title')}</Heading>
            <IconButton Icon={InformationCircleIcon} ariaLabel="Payment information" variant="neutral" size="sm" />
          </TitleContainer>
        </SectionHeader>
        <ContentCard>
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500" />
          </div>
        </ContentCard>
      </Section>
    );
  }

  if (isError || !data) {
    return null;
  }

  const isUnpaidInvoice = data.type === 'unpaid';
  const hasCreditsApplied = data.credit_days_used > 0 && data.credit_discount_amount > 0;

  return (
    <>
      <Section>
        <SectionHeader>
          <TitleContainer>
            <Heading color="neutral">{t('next_payment_title')}</Heading>
          </TitleContainer>
        </SectionHeader>
        <ContentCard>
          <DataCardsContainer>
            <DataCard>
              <DataCardHeader>
                <DataValue>{data.units_consumed ?? 0}</DataValue>
                {hasCreditsApplied && (
                  <CreditBadge>{t('credit_days_badge', { count: data.credit_days_used })}</CreditBadge>
                )}
              </DataCardHeader>
              <DataLabel>{t('units_consumed')}</DataLabel>
            </DataCard>
            <DataCard>
              <DataCardHeader>
                {hasCreditsApplied && (
                  <OriginalAmount>€{data.amount_before_credits?.toFixed(2) ?? '0.00'}</OriginalAmount>
                )}
                <DataValue>
                  €{(hasCreditsApplied ? data.amount_after_credits : data.amount_to_pay)?.toFixed(2) ?? '0.00'}
                </DataValue>
                {hasCreditsApplied && (
                  <CreditBadge>
                    {t('credit_discount_badge', {
                      count: data.credit_days_used,
                      amount: data.credit_discount_amount.toFixed(2),
                    })}
                  </CreditBadge>
                )}
              </DataCardHeader>
              <DataLabel>
                {data?.tax && data?.tax > 0
                  ? t('amount_to_pay_with_vat', { vat: data?.tax?.toFixed(2) ?? '0.00' })
                  : t('amount_to_pay')}
              </DataLabel>
            </DataCard>
            <DataCard>
              <DataValue>{data.due_date ?? '-'}</DataValue>
              <DataLabel>{t('due_date_label')}</DataLabel>
            </DataCard>
          </DataCardsContainer>
          <Link StartIcon={PlusIcon} newTab={false} onClick={() => setConsumptionDetailOpen(true)}>
            {t('more_details')}
          </Link>
          {isUnpaidInvoice && (
            <ButtonV2
              variant="secondary-danger"
              StartIcon={CreditCardIcon}
              onClick={handlePayInvoice}
              disabled={!data.invoice_id}
              loading={isCreatingCheckout}
              fullwidth
              className={'h-12'}
            >
              {t('pay_invoice')}
            </ButtonV2>
          )}
        </ContentCard>
      </Section>
      <ConsumptionDetailModal />
      <PaymentSuccessModal isOpen={showSuccessModal} onClose={handleCloseSuccessModal} />
    </>
  );
};

export default NextPaymentDetails;

const Section = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div className={cn('flex flex-col gap-4', className)} {...props} />
);

const SectionHeader = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div className={cn('flex h-10 items-center justify-between', className)} {...props} />
);

const TitleContainer = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div className={cn('flex gap-2 items-center w-full', className)} {...props} />
);

const ContentCard = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      'flex flex-col gap-4 items-center justify-end p-4 rounded-2xl shadow-sm border border-[#dedee1] bg-[#fafafa]',
      className
    )}
    {...props}
  />
);

const DataCardsContainer = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div className={cn('flex flex-col gap-4 w-full md:flex-row', className)} {...props} />
);

const DataCard = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn('flex-1 flex flex-col gap-1 items-start justify-end p-4 rounded-lg bg-[#f5f5f6]', className)}
    {...props}
  />
);

const DataValue = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div className={cn('flex gap-2 items-center font-semibold text-xl text-neutral-900', className)} {...props} />
);

const DataLabel = ({ className, ...props }: React.HTMLAttributes<HTMLParagraphElement>) => (
  <p className={cn('font-medium text-sm leading-normal text-[#1c1c33]', className)} {...props} />
);

const DataCardHeader = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn('flex flex-wrap gap-2 items-center', className)} {...props} />
);

const CreditBadge = ({ className, ...props }: React.HTMLAttributes<HTMLSpanElement>) => (
  <span
    className={cn(
      'inline-flex items-center justify-center gap-1 px-2 font-medium text-sm rounded h-[24px] bg-[#e6f1ec] border border-[#00542d] text-[#00542d]',
      className
    )}
    {...props}
  />
);

const OriginalAmount = ({ className, ...props }: React.HTMLAttributes<HTMLSpanElement>) => (
  <span className={cn('font-semibold text-xl line-through text-[#757583]', className)} {...props} />
);
